import { memo } from 'react';
import { constructionTasks, taskParticipant, type ConstructionTask } from './construction-model';

type BackdropProps = { tasks: ConstructionTask[]; colors: Record<string, string> };
const DEFAULT_TASK_COLOR = '#a1ada3';

function project(x: number, y: number, z: number): [number, number] {
  return [790 + (x - z) * 16, 440 + (x + z) * 6 - y * 20];
}

function Block({ x, y, z, color, width = 2, height = 0.9 }: { x: number; y: number; z: number; color: string; width?: number; height?: number }) {
  const points = [[x, y, z], [x + width, y, z], [x + width, y, z + width], [x, y, z + width], [x, y + height, z], [x + width, y + height, z], [x + width, y + height, z + width], [x, y + height, z + width]].map(([px, py, pz]) => project(px, py, pz).join(','));
  const face = (indices: number[]) => indices.map(index => points[index]).join(' ');
  return <g fill={color} stroke="#091417" strokeWidth="0.8">
    <polygon points={face([0, 1, 5, 4])} fillOpacity="0.55" />
    <polygon points={face([1, 2, 6, 5])} fillOpacity="0.72" />
    <polygon points={face([2, 3, 7, 6])} fillOpacity="0.68" />
    <polygon points={face([4, 5, 6, 7])} />
  </g>;
}

const StaticSite = memo(function StaticSite() {
  const towers = Array.from({ length: 96 }, (_, index) => ({ x: (index % 12 - 6) * 6, z: (Math.floor(index / 12) - 6) * 6, height: 2 + (index * 17 % 9) }));
  return <g>{towers.filter(tower => Math.abs(tower.x) > 9 || tower.z < -9).map((tower, index) => <g key={index}>{Array.from({ length: tower.height }, (_, level) => <Block key={level} x={tower.x} y={level} z={tower.z} width={4.5} color={index % 3 ? '#637271' : '#85918a'} />)}</g>)}</g>;
});

function ConstructionBackdrop({ tasks, colors }: BackdropProps) {
  return <svg viewBox="0 0 1536 1000" preserveAspectRatio="xMidYMid slice" width="100%" height="100%" aria-hidden="true" focusable="false">
    <defs><linearGradient id="construction-haze" x2="0" y2="1"><stop stopColor="#080e12" stopOpacity="0.97"/><stop offset="0.5" stopColor="#080e12" stopOpacity="0.08"/><stop offset="1" stopColor="#080e12" stopOpacity="0.7"/></linearGradient></defs>
    <StaticSite />
    <rect width="1536" height="1000" fill="url(#construction-haze)" />
    {constructionTasks(tasks).map(({ task, position: [x, y, z] }) => <Block key={task.id} x={x} y={y} z={z} color={colors[taskParticipant(task)] || DEFAULT_TASK_COLOR} />)}
  </svg>;
}

// Polling replaces room arrays and palettes even when their contents are unchanged.
// Compare the values used by this static rendering, not those object identities.
export default memo(ConstructionBackdrop, (previous, next) => {
  if (previous.tasks === next.tasks && previous.colors === next.colors) return true;
  if (previous.tasks.length !== next.tasks.length) return false;
  return previous.tasks.every((task, index) => {
    const other = next.tasks[index];
    return task.id === other.id && task.createdAt === other.createdAt
      && (previous.colors[taskParticipant(task)] || DEFAULT_TASK_COLOR)
        === (next.colors[taskParticipant(other)] || DEFAULT_TASK_COLOR);
  });
});
